/**
 * Star Bright WebMCP — browser tools for AI agents on the storefront.
 * Catalog: Storefront GraphQL when token set, else Online Store JSON/Ajax APIs.
 * Cart: Shopify Cart Ajax (/cart.js, /cart/add.js, …). Checkout: navigate only.
 */
(function () {
  'use strict';

  const cfg = window.__STARBRIGHT_WEBMCP__;
  if (!cfg || cfg.enabled === false) return;

  const modelContext = document.modelContext || navigator.modelContext;
  const supported = !!(modelContext && typeof modelContext.registerTool === 'function');

  const ALLOWED_PATH =
    /^\/($|cart\/?$|collections(\/|$)|products(\/|$)|pages\/(how-it-works|contact|policies)\/?$|search)/i;

  const readOnly = { readOnlyHint: true, untrustedContentHint: false };
  const mutating = { readOnlyHint: false, untrustedContentHint: true };

  function money(cents, currency) {
    if (cents == null) return null;
    const n = Number(cents) / 100;
    return { amount: n.toFixed(2), currency: currency || cfg.currency || 'USD' };
  }

  async function cartGet() {
    const res = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart.js failed: ' + res.status);
    return res.json();
  }

  async function cartPost(path, body) {
    const res = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || path + ' failed: ' + res.status);
    return data;
  }

  function summarizeCart(cart) {
    return {
      item_count: cart.item_count,
      total_price: money(cart.total_price, cart.currency),
      items: (cart.items || []).map((i) => ({
        key: i.key,
        variant_id: i.variant_id,
        product_id: i.product_id,
        handle: i.handle,
        title: i.product_title || i.title,
        variant_title: i.variant_title,
        quantity: i.quantity,
        line_price: money(i.line_price, cart.currency),
        url: i.url,
      })),
      note: cart.note || null,
      cart_url: '/cart',
      checkout_url: '/checkout',
    };
  }

  async function storefrontGraphql(query, variables) {
    const token = cfg.storefrontToken;
    if (!token) return null;
    const endpoint =
      cfg.storefrontEndpoint ||
      'https://' + cfg.shopDomain + '/api/' + (cfg.apiVersion || '2025-01') + '/graphql.json';
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors?.length) throw new Error(json.errors.map((e) => e.message).join('; '));
    return json.data;
  }

  async function productsFromJson(query) {
    const res = await fetch('/products.json?limit=250', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('products.json failed: ' + res.status);
    const data = await res.json();
    let products = data.products || [];
    if (query) {
      const q = String(query).toLowerCase();
      products = products.filter((p) => {
        const hay = [p.title, p.handle, p.product_type, p.vendor, ...(p.tags || [])].join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    return products.map(normalizeJsonProduct);
  }

  function normalizeJsonProduct(p) {
    const variants = (p.variants || []).map((v) => ({
      id: v.id,
      title: v.title,
      sku: v.sku,
      available: v.available,
      price: money(Math.round(parseFloat(v.price) * 100), cfg.currency),
      option1: v.option1,
      option2: v.option2,
      option3: v.option3,
    }));
    const min = variants.reduce((m, v) => Math.min(m, parseFloat(v.price?.amount || '0')), Infinity);
    return {
      id: p.id,
      handle: p.handle,
      title: p.title,
      product_type: p.product_type,
      tags: p.tags || [],
      url: '/products/' + p.handle,
      description: (p.body_html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 600),
      price_from: Number.isFinite(min) ? { amount: min.toFixed(2), currency: cfg.currency } : null,
      variants,
      images: (p.images || []).slice(0, 3).map((img) => img.src),
    };
  }

  async function searchProducts({ query } = {}) {
    const data = await storefrontGraphql(
      `query search($q: String!) {
        products(first: 25, query: $q) {
          nodes {
            id handle title description productType tags
            priceRange { minVariantPrice { amount currencyCode } }
            variants(first: 50) {
              nodes {
                id title availableForSale sku
                price { amount currencyCode }
                selectedOptions { name value }
              }
            }
            images(first: 3) { nodes { url } }
          }
        }
      }`,
      { q: query || '*' },
    );
    if (data?.products?.nodes) {
      return {
        source: 'storefront_graphql',
        products: data.products.nodes.map((p) => ({
          id: p.id,
          handle: p.handle,
          title: p.title,
          product_type: p.productType,
          tags: p.tags,
          url: '/products/' + p.handle,
          description: (p.description || '').slice(0, 600),
          price_from: p.priceRange?.minVariantPrice
            ? {
                amount: p.priceRange.minVariantPrice.amount,
                currency: p.priceRange.minVariantPrice.currencyCode,
              }
            : null,
          variants: (p.variants?.nodes || []).map((v) => ({
            id: v.id,
            numeric_id: Number(String(v.id).split('/').pop()),
            title: v.title,
            available: v.availableForSale,
            sku: v.sku,
            price: v.price,
            options: v.selectedOptions,
          })),
          images: (p.images?.nodes || []).map((i) => i.url),
        })),
      };
    }
    return { source: 'products_json', products: await productsFromJson(query) };
  }

  async function getProduct({ handle }) {
    if (!handle) throw new Error('handle is required');
    const data = await storefrontGraphql(
      `query product($handle: String!) {
        product(handle: $handle) {
          id handle title descriptionHtml productType tags
          priceRange { minVariantPrice { amount currencyCode } maxVariantPrice { amount currencyCode } }
          options { name values }
          variants(first: 100) {
            nodes {
              id title availableForSale sku
              price { amount currencyCode }
              selectedOptions { name value }
            }
          }
          images(first: 6) { nodes { url altText } }
        }
      }`,
      { handle },
    );
    if (data?.product) {
      const p = data.product;
      return {
        source: 'storefront_graphql',
        product: {
          id: p.id,
          handle: p.handle,
          title: p.title,
          product_type: p.productType,
          tags: p.tags,
          url: '/products/' + p.handle,
          description_html: p.descriptionHtml,
          options: p.options,
          price_range: {
            min: p.priceRange?.minVariantPrice,
            max: p.priceRange?.maxVariantPrice,
          },
          variants: (p.variants?.nodes || []).map((v) => ({
            id: v.id,
            numeric_id: Number(String(v.id).split('/').pop()),
            title: v.title,
            available: v.availableForSale,
            sku: v.sku,
            price: v.price,
            options: v.selectedOptions,
          })),
          images: (p.images?.nodes || []).map((i) => ({ url: i.url, alt: i.altText })),
          note: 'Custom apparel: mockup before production. Starting prices; final price depends on design.',
        },
      };
    }
    const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('Product not found: ' + handle);
    const p = await res.json();
    return {
      source: 'product_js',
      product: {
        ...normalizeJsonProduct(p),
        description_html: p.description,
        options: p.options,
        note: 'Custom apparel: mockup before production. Starting prices; final price depends on design.',
      },
    };
  }

  async function listCollections() {
    const data = await storefrontGraphql(
      `query {
        collections(first: 50) {
          nodes {
            id handle title description
            productsCount { count }
          }
        }
      }`,
    );
    if (data?.collections?.nodes) {
      return {
        source: 'storefront_graphql',
        collections: data.collections.nodes.map((c) => ({
          handle: c.handle,
          title: c.title,
          description: (c.description || '').slice(0, 300),
          products_count: c.productsCount?.count,
          url: '/collections/' + c.handle,
        })),
      };
    }
    const res = await fetch('/collections.json?limit=50', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('collections.json failed: ' + res.status);
    const json = await res.json();
    return {
      source: 'collections_json',
      collections: (json.collections || []).map((c) => ({
        handle: c.handle,
        title: c.title,
        description: (c.body_html || '').replace(/<[^>]+>/g, ' ').trim().slice(0, 300),
        products_count: c.products_count,
        url: '/collections/' + c.handle,
      })),
    };
  }

  function getShopInfo() {
    return {
      name: cfg.shopName,
      domain: cfg.primaryDomain || cfg.shopDomain,
      myshopify: cfg.shopDomain,
      currency: cfg.currency,
      contact: cfg.contact,
      how_it_works: cfg.howItWorks,
      shipping: cfg.shipping,
      policies_url: '/pages/policies',
      catalog_note:
        'Starting prices; mockup before production. Custom apparel sales are final. No totes/mugs in catalog yet.',
      webmcp_catalog_mode: cfg.storefrontToken ? 'storefront_graphql' : 'online_store_json',
    };
  }

  function openCartDrawer() {
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      const btn = document.querySelector('cart-drawer-component, [data-cart-drawer], cart-icon-button, .header-actions__cart-icon');
      if (btn && typeof btn.click === 'function') btn.click();
    } catch (_) {
      /* ignore */
    }
  }

  async function addToCart({ variant_id, quantity }) {
    const id = Number(variant_id);
    if (!id) throw new Error('variant_id (numeric Shopify variant id) is required');
    const qty = Math.max(1, Number(quantity) || 1);
    await cartPost('/cart/add.js', { items: [{ id, quantity: qty }] });
    openCartDrawer();
    return summarizeCart(await cartGet());
  }

  async function updateCartLine({ line_key, quantity }) {
    if (!line_key) throw new Error('line_key is required (from get_cart items[].key)');
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty < 0) throw new Error('quantity must be >= 0');
    await cartPost('/cart/change.js', { id: line_key, quantity: qty });
    return summarizeCart(await cartGet());
  }

  async function removeFromCart({ line_key }) {
    return updateCartLine({ line_key, quantity: 0 });
  }

  async function clearCart() {
    await fetch('/cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    return summarizeCart(await cartGet());
  }

  function openCheckout() {
    window.location.href = '/checkout';
    return {
      effect: 'Navigating to Shopify checkout. Order is not placed until the customer completes checkout.',
      url: '/checkout',
    };
  }

  function navigateTo({ path }) {
    if (!path || typeof path !== 'string') throw new Error('path is required');
    let p = path.trim();
    if (!p.startsWith('/')) p = '/' + p;
    if (!ALLOWED_PATH.test(p)) {
      throw new Error(
        'Path not allowed. Use /, /cart, /collections..., /products..., /pages/how-it-works|contact|policies, /search',
      );
    }
    window.location.href = p;
    return { effect: 'Navigating', path: p };
  }

  const tools = [
    {
      name: 'search_products',
      description:
        'Search Star Bright Design catalog (tees, hoodies, polos, name tags). Returns handles, prices, variants. Use get_product for full detail. Custom work is mockup-before-production.',
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Free-text search (e.g. polo, hoodie, name tag)' },
        },
      },
      annotations: readOnly,
      execute: async (args) => JSON.stringify(await searchProducts(args || {})),
    },
    {
      name: 'get_product',
      description:
        'Full product detail by handle, including variants (use numeric variant id with add_to_cart).',
      inputSchema: {
        type: 'object',
        properties: {
          handle: { type: 'string', description: 'Product handle, e.g. gildan-unisex-t-shirt' },
        },
        required: ['handle'],
      },
      annotations: readOnly,
      execute: async (args) => JSON.stringify(await getProduct(args)),
    },
    {
      name: 'list_collections',
      description: 'List store collections (T-Shirts, Sweatshirts & Hoodies, Polos, Name Tags, etc.).',
      inputSchema: { type: 'object', properties: {} },
      annotations: readOnly,
      execute: async () => JSON.stringify(await listCollections()),
    },
    {
      name: 'get_shop_info',
      description:
        'Shop name, contact, shipping blurb, how-it-works summary. Call for business context before recommending.',
      inputSchema: { type: 'object', properties: {} },
      annotations: readOnly,
      execute: async () => JSON.stringify(getShopInfo()),
    },
    {
      name: 'get_cart',
      description: 'Current cart lines, quantities, and totals.',
      inputSchema: { type: 'object', properties: {} },
      annotations: readOnly,
      execute: async () => JSON.stringify(summarizeCart(await cartGet())),
    },
    {
      name: 'add_to_cart',
      description:
        'Add a variant to the cart by numeric variant_id from get_product / search_products. Returns updated cart.',
      inputSchema: {
        type: 'object',
        properties: {
          variant_id: { type: 'number', description: 'Numeric Shopify variant id' },
          quantity: { type: 'number', description: 'Quantity (default 1)' },
        },
        required: ['variant_id'],
      },
      annotations: mutating,
      execute: async (args) => JSON.stringify(await addToCart(args)),
    },
    {
      name: 'update_cart_line',
      description: 'Set quantity for a cart line (0 removes). Use items[].key from get_cart.',
      inputSchema: {
        type: 'object',
        properties: {
          line_key: { type: 'string' },
          quantity: { type: 'number' },
        },
        required: ['line_key', 'quantity'],
      },
      annotations: mutating,
      execute: async (args) => JSON.stringify(await updateCartLine(args)),
    },
    {
      name: 'remove_from_cart',
      description: 'Remove a cart line by items[].key from get_cart.',
      inputSchema: {
        type: 'object',
        properties: { line_key: { type: 'string' } },
        required: ['line_key'],
      },
      annotations: mutating,
      execute: async (args) => JSON.stringify(await removeFromCart(args)),
    },
    {
      name: 'clear_cart',
      description: 'Empty the cart.',
      inputSchema: { type: 'object', properties: {} },
      annotations: mutating,
      execute: async () => JSON.stringify(await clearCart()),
    },
    {
      name: 'open_checkout',
      description:
        'Navigate to Shopify checkout. Only call when the user explicitly asks to check out / place order. Does not place the order by itself.',
      inputSchema: { type: 'object', properties: {} },
      annotations: mutating,
      execute: async () => JSON.stringify(openCheckout()),
    },
    {
      name: 'navigate_to',
      description:
        'Navigate to an allowlisted storefront path (/, /cart, /collections/..., /products/..., /pages/how-it-works|contact|policies).',
      inputSchema: {
        type: 'object',
        properties: { path: { type: 'string' } },
        required: ['path'],
      },
      annotations: mutating,
      execute: async (args) => JSON.stringify(navigateTo(args)),
    },
  ];

  let registered = 0;

  async function registerAll() {
    if (!supported) {
      renderBadge(false, 0);
      return;
    }
    for (const tool of tools) {
      try {
        await modelContext.registerTool(tool);
        registered += 1;
      } catch (err) {
        console.warn('[starbright-webmcp] register failed', tool.name, err);
      }
    }
    renderBadge(true, registered);
  }

  function renderBadge(ok, count) {
    if (document.getElementById('sb-webmcp-badge')) return;
    const el = document.createElement('div');
    el.id = 'sb-webmcp-badge';
    el.setAttribute('aria-live', 'polite');
    el.style.cssText = [
      'position:fixed',
      'bottom:1rem',
      'right:1rem',
      'z-index:9999',
      'font:600 12px/1.2 system-ui,sans-serif',
      'letter-spacing:0.04em',
      'padding:0.55rem 0.75rem',
      'border-radius:6px',
      'border:1px solid #C5A059',
      'background:#FDFBF7',
      'color:#1A1A1A',
      'box-shadow:0 8px 24px rgba(26,26,26,0.08)',
      ok ? '' : 'opacity:0.55',
    ]
      .filter(Boolean)
      .join(';');
    el.textContent = ok ? 'WebMCP · ' + count + ' tools' : 'WebMCP unavailable';
    el.title = ok
      ? 'Star Bright WebMCP tools registered for browser AI agents'
      : 'Enable Chrome WebMCP flag (chrome://flags/#enable-webmcp-testing)';
    document.body.appendChild(el);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      void registerAll();
    });
  } else {
    void registerAll();
  }
})();
